import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../PublicLayout';
import { Mail, Linkedin, Twitter, Facebook } from 'lucide-react';

const Leadership = () => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchLeaders();
  }, []);

  const fetchLeaders = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        setError('API configuration error');
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_BASE}/leadership/public`);
      const data = await response.json();

      if (data.success) {
        setLeaders(data.data || []);
      } else {
        setError('Failed to load leadership team');
      }
    } catch (err) {
      console.error('Error fetching leaders:', err);
      setError('Failed to load leadership team');
    } finally {
      setLoading(false);
    }
  };

  // Group leaders by category - flexible grouping
  const groupedLeaders = {
    'Board of Trustees': [],
    'Executives': [],
    'Country Directors - Africa': [],
    'Country Directors - International': [],
    'Advisory': []
  };

  leaders.forEach(leader => {
    const role = leader.role_position || '';
    const roleLower = role.toLowerCase();

    if (role === 'Board of Trustees' || roleLower.includes('board') || roleLower.includes('trustee')) {
      groupedLeaders['Board of Trustees'].push(leader);
    } else if (role.startsWith('Executive') || roleLower.includes('president') || roleLower.includes('secretary') ||
      roleLower.includes('director') && !roleLower.includes('country') || roleLower.includes('coo') ||
      roleLower.includes('financial') || roleLower.includes('accountant') || roleLower.includes('it consultant')) {
      groupedLeaders['Executives'].push(leader);
    } else if (roleLower.includes('country') && (roleLower.includes('africa') || roleLower.includes('ghana') ||
      roleLower.includes('nigeria') || roleLower.includes('egypt') || roleLower.includes('ethiopia') ||
      roleLower.includes('kenya') || roleLower.includes('liberia') || roleLower.includes('angola') ||
      roleLower.includes('cameroon') || roleLower.includes('guinea') || roleLower.includes('senegal') ||
      roleLower.includes('chad') || roleLower.includes('tanzania') || roleLower.includes('zimbabwe') ||
      roleLower.includes('somalia') || roleLower.includes('burkina') || roleLower.includes('south africa'))) {
      groupedLeaders['Country Directors - Africa'].push(leader);
    } else if (roleLower.includes('country') && (roleLower.includes('international') || roleLower.includes('jamaica') ||
      roleLower.includes('pakistan') || roleLower.includes('guyana') || roleLower.includes('lebanon'))) {
      groupedLeaders['Country Directors - International'].push(leader);
    } else if (role === 'Advisory' || roleLower.includes('advisor') || roleLower.includes('advisory')) {
      groupedLeaders['Advisory'].push(leader);
    } else if (roleLower.includes('country')) {
      // Any other country director defaults to Africa
      groupedLeaders['Country Directors - Africa'].push(leader);
    } else {
      // Default to Executives if no clear category
      groupedLeaders['Executives'].push(leader);
    }
  });

  // Group Country Directors by country (Director + Vice)
  const groupDirectorsByCountry = (directors) => {
    const grouped = {};

    directors.forEach(director => {
      const role = director.role_position || '';

      // Extract country from:
      // "Country Director - Ghana" (new format with hyphen)
      // "Country Director, Ghana" (old format with comma)
      // "Vice Country Director - Ghana"
      const match = role.match(/(?:Vice\s+)?Country\s+Director[,:\s-]+(.+)/i);
      if (match) {
        const country = match[1].trim();

        if (!grouped[country]) {
          grouped[country] = { director: null, vice: null };
        }
        if (role.toLowerCase().includes('vice')) {
          grouped[country].vice = director;
        } else {
          grouped[country].director = director;
        }
      }
    });

    return grouped;
  };

  const LeaderCard = ({ leader }) => {
    // Determine image source - either from uploads or from assets
    const getImageSrc = () => {
      if (!leader.profile_image) return null;

      const API_BASE = process.env.REACT_APP_API_URL;
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      if (!BACKEND_URL) return null;

      // If it starts with /uploads/, it's an uploaded image from API
      if (leader.profile_image.startsWith('/uploads/')) {
        const baseUrl = `${BACKEND_URL}${leader.profile_image}`;
        // Add cache-busting timestamp to force browser to reload updated images
        return `${baseUrl}?v=${Date.now()}`;
      }

      // If it starts with /assets/, it's a static asset - use require
      if (leader.profile_image.startsWith('/assets/')) {
        try {
          // Convert /assets/... to require path
          const assetPath = leader.profile_image.replace('/assets/', '');
          return require(`../../../assets/${assetPath}`);
        } catch (e) {
          console.warn('Image not found:', leader.profile_image);
          return null;
        }
      }

      // If it starts with http:// or https://, it's already a full URL
      if (leader.profile_image.startsWith('http://') || leader.profile_image.startsWith('https://')) {
        return leader.profile_image;
      }

      // Otherwise, it's likely just a filename (like leader-123.jpg)
      // Assume it's in the /uploads/ directory
      const baseUrl = `${BACKEND_URL}/uploads/${leader.profile_image}`;
      return `${baseUrl}?v=${Date.now()}`;
    };

    const imageSrc = getImageSrc();

    return (
      <div className="col-md-2 mb-4">
        <div
          className="card h-100 border-0 shadow-sm"
          style={{
            borderTop: '3px solid #6B8E23',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(107, 142, 35, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '';
          }}
        >
          <div className="card-body text-center p-2">
            <div className="mb-2">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={leader.full_name}
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
              ) : (
                <div
                  className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                  style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#6B8E23',
                    color: 'white',
                    fontSize: '2rem',
                    fontWeight: 'bold'
                  }}
                >
                  {leader.full_name.charAt(0)}
                </div>
              )}
            </div>
            <h6 className="card-title mb-1" style={{ color: '#6B8E23', fontSize: '0.9rem' }}>
              {leader.full_name}
            </h6>
            <p className="text-muted small mb-2" style={{ fontSize: '0.75rem' }}>{leader.role_position}</p>

            <button
              className="btn btn-sm"
              style={{ backgroundColor: '#6B8E23', color: 'white', fontSize: '0.75rem' }}
              onClick={() => {
                setSelectedLeader(leader);
                setShowModal(true);
              }}
            >
              Details
            </button>
          </div>
        </div>
      </div>
    );
  };

  // New component for grouped Country Director card (Director + Vice)
  const CountryDirectorCard = ({ country, director, vice }) => {
    const getImageSrc = (leader) => {
      if (!leader || !leader.profile_image) return null;
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      if (!BACKEND_URL) return null;
      if (leader.profile_image.startsWith('/uploads/')) {
        return `${BACKEND_URL}${leader.profile_image}?v=${Date.now()}`;
      }
      if (leader.profile_image.startsWith('/assets/')) {
        try {
          const assetPath = leader.profile_image.replace('/assets/', '');
          return require(`../../../assets/${assetPath}`);
        } catch (e) {
          return null;
        }
      }
      if (leader.profile_image.startsWith('http')) {
        return leader.profile_image;
      }
      return `${BACKEND_URL}/uploads/${leader.profile_image}?v=${Date.now()}`;
    };

    return (
      <div className="col-md-3 mb-4">
        <div
          className="card h-100 border-0 shadow-sm"
          style={{
            borderTop: '4px solid #6B8E23',
            transition: 'all 0.3s ease',
            backgroundColor: '#f8f9fa'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(107, 142, 35, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '';
          }}
        >
          <div className="card-body p-3">
            {/* Country Header */}
            <div className="text-center mb-3 pb-2" style={{ borderBottom: '2px solid #6B8E23' }}>
              <h6 className="fw-bold mb-0" style={{ color: '#6B8E23', fontSize: '0.95rem' }}>{country}</h6>
            </div>

            {/* Directors Side by Side */}
            <div className="row g-2">
              {/* Country Director */}
              {director && (
                <div className={vice ? "col-6" : "col-12"}>
                  <div className="text-center">
                    <div className="mb-2">
                      {getImageSrc(director) ? (
                        <img
                          src={getImageSrc(director)}
                          alt={director.full_name}
                          className="rounded-circle"
                          style={{ width: '70px', height: '70px', objectFit: 'cover', border: '2px solid #6B8E23' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                          style={{
                            width: '70px',
                            height: '70px',
                            backgroundColor: '#6B8E23',
                            color: 'white',
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {director.full_name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <h6 className="fw-bold mb-1" style={{ color: '#6B8E23', fontSize: '0.8rem' }}>{director.full_name}</h6>
                    <p className="text-muted mb-2" style={{ fontSize: '0.65rem' }}><strong>Director</strong></p>
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: '#6B8E23', color: 'white', fontSize: '0.65rem', padding: '4px 8px' }}
                      onClick={() => {
                        setSelectedLeader(director);
                        setShowModal(true);
                      }}
                    >
                      Profile
                    </button>
                  </div>
                </div>
              )}

              {/* Vice Director */}
              {vice && (
                <div className="col-6">
                  <div className="text-center">
                    <div className="mb-2">
                      {getImageSrc(vice) ? (
                        <img
                          src={getImageSrc(vice)}
                          alt={vice.full_name}
                          className="rounded-circle"
                          style={{ width: '70px', height: '70px', objectFit: 'cover', border: '2px solid #556B2F' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                          style={{
                            width: '70px',
                            height: '70px',
                            backgroundColor: '#556B2F',
                            color: 'white',
                            fontSize: '1.5rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {vice.full_name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <h6 className="fw-bold mb-1" style={{ color: '#556B2F', fontSize: '0.8rem' }}>{vice.full_name}</h6>
                    <p className="text-muted mb-2" style={{ fontSize: '0.65rem' }}><strong>Vice</strong></p>
                    <button
                      className="btn btn-sm"
                      style={{ backgroundColor: '#556B2F', color: 'white', fontSize: '0.65rem', padding: '4px 8px' }}
                      onClick={() => {
                        setSelectedLeader(vice);
                        setShowModal(true);
                      }}
                    >
                      Profile
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <PublicLayout>
      <div className="leadership-page">
        {/* Hero Section */}
        <section className="py-5" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)' }}>
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center">
                <h1 className="display-4 fw-bold mb-3 text-white">
                  Our Leadership
                </h1>
                <p className="lead text-white" style={{ opacity: 0.95 }}>
                  Meet the dedicated individuals guiding ARCA's mission to advance respiratory care across Africa and beyond.
                </p>
              </div>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="container py-5">
            <div className="text-center">
              <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="container py-5">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        ) : (
          <>
            {/* Board of Trustees */}
            {groupedLeaders['Board of Trustees'].length > 0 && (
              <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="container">
                  <div className="text-center mb-5">
                    <h2 className="fw-bold" style={{ color: '#6B8E23' }}>Board of Trustees</h2>
                    <p className="text-muted">Our governing body providing strategic guidance and oversight</p>
                  </div>
                  <div className="row">
                    {groupedLeaders['Board of Trustees'].map(leader => (
                      <LeaderCard key={leader.id} leader={leader} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Executives */}
            {groupedLeaders['Executives'].length > 0 && (
              <section className="py-5" style={{ backgroundColor: '#ffffff' }}>
                <div className="container">
                  <div className="text-center mb-5">
                    <h2 className="fw-bold" style={{ color: '#6B8E23' }}>Executive Team</h2>
                    <p className="text-muted">Leading our day-to-day operations and strategic initiatives</p>
                  </div>
                  <div className="row">
                    {groupedLeaders['Executives'].map(leader => (
                      <LeaderCard key={leader.id} leader={leader} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Africa Country Directors */}
            {groupedLeaders['Country Directors - Africa'].length > 0 && (
              <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="container">
                  <div className="text-center mb-5">
                    <h2 className="fw-bold" style={{ color: '#6B8E23' }}>Africa Country Directors</h2>
                    <p className="text-muted">Leading ARCA chapters across African nations</p>
                  </div>
                  <div className="row">
                    {Object.entries(groupDirectorsByCountry(groupedLeaders['Country Directors - Africa'])).map(([country, { director, vice }]) => (
                      <CountryDirectorCard key={country} country={country} director={director} vice={vice} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* International Country Directors */}
            {groupedLeaders['Country Directors - International'].length > 0 && (
              <section className="py-5" style={{ backgroundColor: '#ffffff' }}>
                <div className="container">
                  <div className="text-center mb-5">
                    <h2 className="fw-bold" style={{ color: '#6B8E23' }}>International Country Directors</h2>
                    <p className="text-muted">Extending ARCA's reach beyond Africa</p>
                  </div>
                  <div className="row">
                    {Object.entries(groupDirectorsByCountry(groupedLeaders['Country Directors - International'])).map(([country, { director, vice }]) => (
                      <CountryDirectorCard key={country} country={country} director={director} vice={vice} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Advisory Board */}
            {groupedLeaders['Advisory'].length > 0 && (
              <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="container">
                  <div className="text-center mb-5">
                    <h2 className="fw-bold" style={{ color: '#6B8E23' }}>Advisory Board</h2>
                    <p className="text-muted">Providing expert guidance and strategic counsel</p>
                  </div>
                  <div className="row">
                    {groupedLeaders['Advisory'].map(leader => (
                      <LeaderCard key={leader.id} leader={leader} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {leaders.length === 0 && (
              <div className="container py-5">
                <div className="alert alert-info text-center" role="alert">
                  No leadership members found.
                </div>
              </div>
            )}
          </>
        )}

        {/* Call to Action */}
        <section className="py-5" style={{ backgroundColor: '#6B8E23' }}>
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center text-white">
                <h2 className="fw-bold mb-3">Join Our Mission</h2>
                <p className="lead mb-4">
                  Interested in contributing to respiratory care advancement in Africa?
                </p>
                <Link to="/contact" className="btn btn-light btn-lg">
                  Get in Touch
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Leader Details Modal */}
        {showModal && selectedLeader && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowModal(false)}>
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                  <h5 className="modal-title">{selectedLeader.full_name}</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="text-center mb-3">
                    {(() => {
                      const getImageSrc = () => {
                        if (!selectedLeader.profile_image) return null;

                        const API_BASE = process.env.REACT_APP_API_URL;
                        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                        if (!BACKEND_URL) return null;

                        if (selectedLeader.profile_image.startsWith('/uploads/')) {
                          const baseUrl = `${BACKEND_URL}${selectedLeader.profile_image}`;
                          // Add cache-busting for modal image
                          return `${baseUrl}?v=${Date.now()}`;
                        }
                        if (selectedLeader.profile_image.startsWith('/assets/')) {
                          try {
                            const assetPath = selectedLeader.profile_image.replace('/assets/', '');
                            return require(`../../../assets/${assetPath}`);
                          } catch (e) {
                            return null;
                          }
                        }
                        // If it starts with http:// or https://, it's already a full URL
                        if (selectedLeader.profile_image.startsWith('http://') || selectedLeader.profile_image.startsWith('https://')) {
                          return selectedLeader.profile_image;
                        }
                        // Otherwise, it's likely just a filename (like leader-123.jpg)
                        // Assume it's in the /uploads/ directory
                        const baseUrl = `${BACKEND_URL}/uploads/${selectedLeader.profile_image}`;
                        return `${baseUrl}?v=${Date.now()}`;
                      };
                      const imageSrc = getImageSrc();
                      return imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={selectedLeader.full_name}
                          className="rounded-circle"
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle mx-auto d-flex align-items-center justify-content-center"
                          style={{
                            width: '150px',
                            height: '150px',
                            backgroundColor: '#6B8E23',
                            color: 'white',
                            fontSize: '4rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {selectedLeader.full_name.charAt(0)}
                        </div>
                      );
                    })()}
                  </div>
                  <h5 className="text-center" style={{ color: '#6B8E23' }}>{selectedLeader.role_position}</h5>

                  {selectedLeader.bio && (
                    <div className="mt-3">
                      <h6 className="fw-bold">Biography</h6>
                      <p className="text-muted">{selectedLeader.bio}</p>
                    </div>
                  )}

                  <div className="mt-3">
                    <h6 className="fw-bold">Contact Information</h6>
                    {selectedLeader.email && (
                      <p className="mb-1">
                        <Mail size={16} className="me-2" style={{ color: '#6B8E23' }} />
                        <a href={`mailto:${selectedLeader.email}`} className="text-decoration-none" style={{ color: '#6B8E23' }}>
                          {selectedLeader.email}
                        </a>
                      </p>
                    )}
                    {selectedLeader.phone && (
                      <p className="mb-1">
                        <strong>Phone:</strong> {selectedLeader.phone}
                      </p>
                    )}
                  </div>

                  {(selectedLeader.linkedin_url || selectedLeader.twitter_url || selectedLeader.facebook_url) && (
                    <div className="mt-3">
                      <h6 className="fw-bold">Social Media</h6>
                      <div className="d-flex gap-3">
                        {selectedLeader.linkedin_url && (
                          <a href={selectedLeader.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: '#6B8E23' }}>
                            <Linkedin size={24} />
                          </a>
                        )}
                        {selectedLeader.twitter_url && (
                          <a href={selectedLeader.twitter_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: '#6B8E23' }}>
                            <Twitter size={24} />
                          </a>
                        )}
                        {selectedLeader.facebook_url && (
                          <a href={selectedLeader.facebook_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ color: '#6B8E23' }}>
                            <Facebook size={24} />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default Leadership;
